import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { User, Phone, Mail, Calendar, AlertTriangle, CheckCircle, Edit, Building2, Star, TrendingUp, DollarSign, Store, Save, X, Shield, Clock, Activity, MessageSquare, MapPin, Package, Truck } from 'lucide-react'
import { api } from '../../lib/api'
import AdminNavHeader from '../../components/AdminNavHeader'
import ProfilePhoto from '../../components/ProfilePhoto'
import toast from 'react-hot-toast'
import { getCache, getCacheStale, setCache } from '../../lib/cache'

interface StoreInfo {
  id: string
  name: string
  slug?: string
  status: string
  is_active: boolean
  address?: string
  phone?: string
  email?: string
  created_at?: string
  approved_at?: string
  rating?: number
  review_count?: number
}

interface UserDetails {
  id: string
  name: string
  email: string
  phone: string
  type: 'customer' | 'store_owner' | 'store_admin' | 'dispatcher' | 'chef' | 'delivery' | 'platform_admin'
  is_active: boolean
  email_verified: boolean
  created_at: string
  updated_at: string
  last_login_at?: string
  store_id?: string
  profile_photo_url?: string
  delivery_available?: boolean
  development_access?: boolean
  total_orders?: number
  total_spent?: number
  employee_orders?: number
  completed_orders_30d?: number
  store?: StoreInfo
  owned_stores?: StoreInfo[]
}

const UserDetails = () => {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const [user, setUser] = useState<UserDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    type: 'customer' as UserDetails['type'],
    store_id: '' as string | null
  })
  const [stores, setStores] = useState<Array<{ id: string; name: string; status: string; is_active: boolean }>>([])
  const [loadingStores, setLoadingStores] = useState(false)

  useEffect(() => {
    if (userId) {
      // Check if we're in edit mode
      const isEditMode = location.pathname.endsWith('/edit')
      setIsEditing(isEditMode)
      loadUserDetails()
    }
  }, [userId, location.pathname])

  useEffect(() => {
    if (user) {
      setEditForm({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        type: user.type || 'customer',
        store_id: user.store_id || null
      })
    }
  }, [user])

  useEffect(() => {
    loadStores()
  }, [])

  const loadUserDetails = async () => {
    if (!userId) return
    
    const cacheKey = `admin_user_details_${userId}`
    
    // Intentar cargar desde cache primero
    const cachedUser = getCacheStale<any>(cacheKey)
    if (cachedUser) {
      setUser(cachedUser)
      setLoading(false)
    }

    try {
      setLoading(true)
      const response = await api.get(`/admin/users/${userId}`)
      if (response.data && response.data.data) {
        setUser(response.data.data)
        
        // Guardar en cache (5 minutos de TTL)
        setCache(cacheKey, response.data.data, 5 * 60 * 1000)
      } else {
        navigate('/admin/users')
      }
    } catch (error: any) {
      console.error('Error loading user details:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedUser) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      navigate('/admin/users')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleSaveEdit = async () => {
    if (!user) return

    try {
      // Prepare data: convert empty string to null for store_id
      const updateData = {
        ...editForm,
        store_id: editForm.store_id === '' || editForm.store_id === null ? null : editForm.store_id
      }

      const response = await api.patch(`/admin/users/${user.id}`, updateData)
      if (response.data.success) {
        toast.success('Usuario actualizado exitosamente')
        // Reload user details to get updated store info
        await loadUserDetails()
        setIsEditing(false)
      }
    } catch (error: any) {
      console.error('Error updating user:', error)
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors?.[0]?.msg || 
                          'Error al actualizar usuario'
      toast.error(errorMessage)
    }
  }

  const loadStores = async () => {
    try {
      setLoadingStores(true)
      const response = await api.get('/admin/stores?limit=1000&status=approved')
      if (response.data.success) {
        setStores(response.data.data || [])
      }
    } catch (error) {
      console.error('Error loading stores:', error)
    } finally {
      setLoadingStores(false)
    }
  }

  const handleCancelEdit = () => {
    setEditForm({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      type: user?.type || 'customer',
      store_id: user?.store_id || null
    })
    setIsEditing(false)
  }

  const handleDevelopmentAccessChange = async (userId: string, developmentAccess: boolean) => {
    if (!user) return
    
    // Optimistic update - actualizar el estado inmediatamente
    const previousValue = user.development_access || false
    setUser({ ...user, development_access: developmentAccess })
    
    try {
      const response = await api.toggleUserDevelopmentAccess(userId, developmentAccess)
      if (response.success) {
        toast.success(response.message || `Acceso a desarrollo ${developmentAccess ? 'concedido' : 'revocado'}`)
        // Recargar para asegurar sincronización con el backend
        await loadUserDetails()
      } else {
        // Revertir si falla
        setUser({ ...user, development_access: previousValue })
        toast.error('Error al cambiar acceso a desarrollo')
      }
    } catch (error: any) {
      console.error('Error changing development access:', error)
      // Revertir el cambio optimista
      setUser({ ...user, development_access: previousValue })
      toast.error(error.response?.data?.message || 'Error al cambiar acceso a desarrollo')
    }
  }

  const handleStatusChange = async (userId: string, isActive: boolean) => {
    try {
      await api.patch(`/admin/users/${userId}/status`, { isActive })
      if (user) {
        setUser({ ...user, is_active: isActive })
      }
    } catch (error) {
      console.error('Error updating user status:', error)
    }
  }

  const handleRoleChange = async (userId: string, newRole: UserDetails['type']) => {
    try {
      await api.patch(`/admin/users/${userId}/role`, { type: newRole })
      if (user) {
        setUser({ ...user, type: newRole })
      }
    } catch (error) {
      console.error('Error updating user role:', error)
    }
  }

  const getRoleColor = (type: string) => {
    switch (type) {
      case 'platform_admin': return 'bg-red-100 text-red-800'
      case 'store_owner': return 'bg-blue-100 text-blue-800'
      case 'store_admin': return 'bg-purple-100 text-purple-800'
      case 'dispatcher': return 'bg-orange-100 text-orange-800'
      case 'chef': return 'bg-green-100 text-green-800'
      case 'delivery': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getRoleLabel = (type: string) => {
    switch (type) {
      case 'platform_admin': return 'Admin Plataforma'
      case 'store_owner': return 'Dueño Negocio'
      case 'store_admin': return 'Admin Negocio'
      case 'dispatcher': return 'Despachador'
      case 'chef': return 'Cocinero'
      case 'delivery': return 'Repartidor'
      default: return 'Cliente'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando detalles...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Usuario no encontrado</h2>
          <p className="text-gray-600 mb-4">El usuario solicitado no existe.</p>
          <button
            onClick={() => navigate('/admin/users')}
            className="btn-primary"
          >
            Volver a Usuarios
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <AdminNavHeader
        title={isEditing ? 'Editar Usuario' : 'Detalles del Usuario'}
        subtitle={user ? `${user.name} - ${user.email}` : ''}
        backTo="/admin/users"
      />

      {/* Content - iMac Style Design - Mobile Responsive */}
      <div className="px-3 sm:px-4 py-4 sm:py-6 lg:py-8 safe-area-bottom lg:px-12 xl:px-16">
        <div className="max-w-7xl mx-auto">
          {isEditing ? (
            /* Edit Mode - iPhone Style Design */
            <div className="space-y-4 sm:space-y-6">
              {/* Edit Header - iOS Style */}
              <div className="card-modern p-4 sm:p-6 lg:p-8">
                <div className="flex items-center space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl flex items-center justify-center shadow-ios-lg shadow-blue-500/20 flex-shrink-0">
                    <User className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Editando Usuario</h2>
                    <p className="text-xs sm:text-sm lg:text-base text-gray-500 mt-1">Modifica la información del usuario</p>
                  </div>
                </div>
              </div>

              {/* Edit Form - iOS Style */}
              <div className="card-modern p-4 sm:p-6 lg:p-8">
                <div className="space-y-4 sm:space-y-6">
                  {/* Name Field */}
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                      Nombre Completo
                    </label>
                    <input
                      type="text"
                      value={editForm.name ?? ''}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="input-ios w-full px-4 py-3 sm:py-3.5 text-sm sm:text-base"
                      placeholder="Ingresa el nombre completo"
                    />
                  </div>

                  {/* Email and Phone Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    {/* Email Field */}
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                        Correo Electrónico
                      </label>
                      <input
                        type="email"
                        value={editForm.email ?? ''}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="input-ios w-full px-4 py-3 sm:py-3.5 text-sm sm:text-base"
                        placeholder="correo@ejemplo.com"
                      />
                    </div>

                    {/* Phone Field */}
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                        Teléfono
                      </label>
                      <input
                        type="tel"
                        value={editForm.phone ?? ''}
                        onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                        className="input-ios w-full px-4 py-3 sm:py-3.5 text-sm sm:text-base"
                        placeholder="809-555-0123"
                      />
                    </div>
                  </div>

                  {/* Role Field */}
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                      Rol del Usuario
                    </label>
                    <select
                      value={editForm.type ?? 'customer'}
                      onChange={(e) => setEditForm({ ...editForm, type: e.target.value as UserDetails['type'] })}
                      className="input-ios w-full px-4 py-3 sm:py-3.5 text-sm sm:text-base appearance-none bg-white"
                    >
                      <option value="customer">Cliente</option>
                      <option value="store_owner">Dueño de Negocio</option>
                      <option value="store_admin">Admin de Negocio</option>
                      <option value="dispatcher">Despachador</option>
                      <option value="chef">Cocinero</option>
                      <option value="delivery">Repartidor</option>
                      <option value="platform_admin">Admin de Plataforma</option>
                    </select>
                  </div>

                  {/* Store Assignment Field - Only show for store-related roles */}
                  {(editForm.type === 'store_admin' || editForm.type === 'dispatcher' || editForm.type === 'chef' || editForm.type === 'delivery') && (
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                        Asignar a Negocio
                      </label>
                      {loadingStores ? (
                        <div className="input-ios w-full px-4 py-3 sm:py-3.5 text-sm sm:text-base text-center text-gray-500 bg-gray-50">
                          Cargando negocios...
                        </div>
                      ) : (
                        <select
                          value={editForm.store_id || ''}
                          onChange={(e) => setEditForm({ ...editForm, store_id: e.target.value || null })}
                          className="input-ios w-full px-4 py-3 sm:py-3.5 text-sm sm:text-base appearance-none bg-white"
                        >
                          <option value="">Sin negocio asignado</option>
                          {stores.map(store => (
                            <option key={store.id} value={store.id}>
                              {store.name} {store.is_active ? '(Activo)' : '(Inactivo)'}
                            </option>
                          ))}
                        </select>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        Selecciona un negocio para asignar este usuario como empleado
                      </p>
                    </div>
                  )}

                  {/* Development Access Toggle - iOS Style */}
                  {user.type !== 'platform_admin' && (
                    <div className="pt-4 sm:pt-6 border-t border-gray-200/50">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 sm:gap-3 mb-1">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-ios shadow-purple-500/20 flex-shrink-0">
                              <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <label className="block text-sm sm:text-base font-semibold text-gray-900">
                                Acceso a Desarrollo
                              </label>
                              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                                Permite ver negocios en modo desarrollo
                              </p>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDevelopmentAccessChange(user.id, !(user.development_access || false))}
                          disabled={!user}
                          className={`relative inline-flex h-7 w-12 sm:h-8 sm:w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                            (user.development_access || false) ? 'bg-gradient-to-r from-purple-500 to-purple-600' : 'bg-gray-300'
                          }`}
                          aria-label="Toggle desarrollo"
                        >
                          <span
                            className={`pointer-events-none inline-block h-6 w-6 sm:h-7 sm:w-7 transform rounded-full bg-white shadow-ios transition duration-200 ease-in-out ${
                              (user.development_access || false) ? 'translate-x-5 sm:translate-x-7' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons - iOS Style */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button
                  onClick={handleCancelEdit}
                  className="flex-1 flex items-center justify-center px-6 py-3.5 sm:py-4 bg-white border-2 border-gray-200 rounded-2xl sm:rounded-3xl text-sm sm:text-base font-semibold text-gray-700 hover:bg-gray-50 active:scale-95 transition-all shadow-ios min-h-[52px]"
                >
                  <X className="w-5 h-5 mr-2" />
                  Cancelar
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="flex-1 flex items-center justify-center px-6 py-3.5 sm:py-4 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl sm:rounded-3xl text-sm sm:text-base font-semibold text-white hover:from-blue-700 hover:to-blue-800 active:scale-95 transition-all shadow-ios-lg shadow-blue-500/30 min-h-[52px]"
                >
                  <Save className="w-5 h-5 mr-2" />
                  Guardar Cambios
                </button>
              </div>
            </div>
          ) : (
            /* View Mode - iMac Style */
            <div className="space-y-8">
              {/* Hero Section - iMac Style - Mobile Responsive */}
              <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-br from-gray-50 to-white px-4 py-6 sm:px-6 sm:py-8 lg:px-12 lg:py-12 xl:px-16 xl:py-16">
                  <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 lg:gap-8 xl:gap-12">
                    <div className="relative flex-shrink-0">
                      {user.profile_photo_url ? (
                        <ProfilePhoto 
                          photoUrl={user.profile_photo_url} 
                          size="xl" 
                          showBorder={true}
                          className="ring-2 sm:ring-4 ring-white shadow-xl sm:shadow-2xl"
                        />
                      ) : (
                        <div className="w-24 h-24 sm:w-32 sm:h-32 lg:w-40 lg:h-40 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl sm:rounded-3xl flex items-center justify-center shadow-xl sm:shadow-2xl">
                          <User className="w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 text-center sm:text-left">
                      <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-2 sm:mb-3 tracking-tight break-words">{user.name}</h1>
                      <p className="text-sm sm:text-base lg:text-lg xl:text-xl text-gray-600 mb-4 sm:mb-6 break-all">{user.email}</p>
                      <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-3">
                        <span className={`inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold ${getRoleColor(user.type)} shadow-sm`}>
                          <Shield className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                          {getRoleLabel(user.type)}
                        </span>
                        <span className={`inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold shadow-sm ${
                          user.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {user.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                        {user.email_verified && (
                          <span className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold bg-blue-100 text-blue-800 shadow-sm">
                            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                            Email Verificado
                          </span>
                        )}
                        {user.type === 'delivery' && user.delivery_available && (
                          <span className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold bg-yellow-100 text-yellow-800 shadow-sm">
                            <Truck className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                            Disponible
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
                      <button
                        onClick={handleEdit}
                        className="flex-1 sm:flex-none flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl active:scale-95"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        <span className="hidden sm:inline">Editar</span>
                        <span className="sm:hidden">Editar</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Main Content Grid - iMac Style */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column - Main Information */}
                <div className="lg:col-span-2 space-y-8">

                  {/* Personal Information - iMac Style */}
                  <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 lg:p-12">
                    <div className="flex items-center mb-8 pb-6 border-b border-gray-200">
                      <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center mr-4">
                        <User className="w-6 h-6 text-blue-600" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900">Información Personal</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100">
                        <div className="flex items-center mb-2 sm:mb-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg sm:rounded-xl flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                            <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                          </div>
                          <p className="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wide">Email</p>
                        </div>
                        <p className="text-sm sm:text-base lg:text-lg font-medium text-gray-900 break-all">{user.email}</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100">
                        <div className="flex items-center mb-2 sm:mb-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-lg sm:rounded-xl flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                            <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                          </div>
                          <p className="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wide">Teléfono</p>
                        </div>
                        <p className="text-sm sm:text-base lg:text-lg font-medium text-gray-900">{user.phone || 'No especificado'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100">
                        <div className="flex items-center mb-2 sm:mb-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-lg sm:rounded-xl flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                            <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                          </div>
                          <p className="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wide">Registrado</p>
                        </div>
                        <p className="text-sm sm:text-base lg:text-lg font-medium text-gray-900">
                          {new Date(user.created_at).toLocaleDateString('es-DO', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                      {user.updated_at && (
                        <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100">
                          <div className="flex items-center mb-2 sm:mb-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-lg sm:rounded-xl flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                            </div>
                            <p className="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wide">Última Actualización</p>
                          </div>
                          <p className="text-sm sm:text-base lg:text-lg font-medium text-gray-900">
                            {new Date(user.updated_at).toLocaleDateString('es-DO', {
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      )}
                      {user.last_login_at && (
                        <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100">
                          <div className="flex items-center mb-2 sm:mb-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-indigo-100 rounded-lg sm:rounded-xl flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                              <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                            </div>
                            <p className="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wide">Último Login</p>
                          </div>
                          <p className="text-sm sm:text-base lg:text-lg font-medium text-gray-900">
                            {new Date(user.last_login_at).toLocaleDateString('es-DO', {
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      )}
                      {user.type === 'delivery' && (
                        <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100">
                          <div className="flex items-center mb-2 sm:mb-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-yellow-100 rounded-lg sm:rounded-xl flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                              <Truck className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" />
                            </div>
                            <p className="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wide">Disponibilidad</p>
                          </div>
                          <p className="text-sm sm:text-base lg:text-lg font-medium text-gray-900">
                            {user.delivery_available ? 'Disponible para Entrega' : 'No Disponible'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Business/Work Information - iMac Style - Mobile Responsive */}
                  {(user.type === 'store_owner' || user.type === 'store_admin' || user.type === 'dispatcher' || user.type === 'chef' || user.type === 'delivery') && (
                    <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 p-4 sm:p-6 lg:p-8 xl:p-12">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 lg:mb-8 pb-4 sm:pb-6 border-b border-gray-200 gap-3 sm:gap-0">
                        <div className="flex items-center">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-50 rounded-xl sm:rounded-2xl flex items-center justify-center mr-3 sm:mr-4">
                            <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                          </div>
                          <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Información Laboral / Negocio</h2>
                        </div>
                        {!user.store && !(user.owned_stores && user.owned_stores.length > 0) && (user.type === 'store_admin' || user.type === 'dispatcher' || user.type === 'chef' || user.type === 'delivery') && (
                          <button
                            onClick={handleEdit}
                            className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-primary-600 bg-primary-50 rounded-lg sm:rounded-xl hover:bg-primary-100 transition-colors w-full sm:w-auto"
                          >
                            Asignar Negocio
                          </button>
                        )}
                      </div>
                      
                      {/* Show all owned stores if user is store_owner */}
                      {user.type === 'store_owner' && user.owned_stores && user.owned_stores.length > 0 ? (
                        <div className="space-y-4 sm:space-y-6">
                          <div className="mb-4 sm:mb-6">
                            <p className="text-sm sm:text-base font-semibold text-gray-700 mb-2">
                              Total de Negocios: <span className="text-primary-600 font-bold">{user.owned_stores.length}</span>
                            </p>
                          </div>
                          <div className="space-y-4 sm:space-y-6">
                            {user.owned_stores.map((store) => (
                              <div key={store.id} className="bg-gradient-to-br from-blue-50 to-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-blue-200 shadow-sm">
                                <div className="flex items-start justify-between mb-3 sm:mb-4">
                                  <div className="flex-1 min-w-0">
                                    <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-1 sm:mb-2">{store.name}</h3>
                                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                      <span className={`inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-bold ${
                                        store.status === 'approved'
                                          ? 'bg-green-200 text-green-800'
                                          : store.status === 'pending'
                                          ? 'bg-yellow-200 text-yellow-800'
                                          : 'bg-red-200 text-red-800'
                                      }`}>
                                        {store.status === 'approved' ? 'Aprobado' :
                                         store.status === 'pending' ? 'Pendiente' : 'Rechazado'}
                                      </span>
                                      <span className={`inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-bold ${
                                        store.is_active
                                          ? 'bg-green-100 text-green-800'
                                          : 'bg-red-100 text-red-800'
                                      }`}>
                                        {store.is_active ? 'Activo' : 'Inactivo'}
                                      </span>
                                      {store.rating && (
                                        <span className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-bold bg-yellow-100 text-yellow-800">
                                          <Star className="w-3 h-3 sm:w-4 sm:h-4 mr-1 fill-current" />
                                          {Number(store.rating).toFixed(1)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                  {store.address && (
                                    <div className="flex items-start">
                                      <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
                                      <div className="min-w-0">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Dirección</p>
                                        <p className="text-sm sm:text-base font-medium text-gray-900">{store.address}</p>
                                      </div>
                                    </div>
                                  )}
                                  {store.phone && (
                                    <div className="flex items-start">
                                      <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
                                      <div className="min-w-0">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Teléfono</p>
                                        <p className="text-sm sm:text-base font-medium text-gray-900">{store.phone}</p>
                                      </div>
                                    </div>
                                  )}
                                  {store.email && (
                                    <div className="flex items-start sm:col-span-2">
                                      <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
                                      <div className="min-w-0">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Email</p>
                                        <p className="text-sm sm:text-base font-medium text-gray-900 break-all">{store.email}</p>
                                      </div>
                                    </div>
                                  )}
                                  {store.created_at && (
                                    <div className="flex items-start">
                                      <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
                                      <div className="min-w-0">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Creado</p>
                                        <p className="text-sm sm:text-base font-medium text-gray-900">
                                          {new Date(store.created_at).toLocaleDateString('es-DO', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric'
                                          })}
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                  {store.review_count !== undefined && (
                                    <div className="flex items-start">
                                      <Star className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
                                      <div className="min-w-0">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Reseñas</p>
                                        <p className="text-sm sm:text-base font-medium text-gray-900">{store.review_count || 0}</p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-blue-200">
                                  <button
                                    onClick={() => navigate(`/admin/stores/${store.id}`)}
                                    className="w-full sm:w-auto px-4 py-2 text-xs sm:text-sm font-semibold text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                                  >
                                    Ver Detalles del Negocio
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : user.store ? (
                        <div className="space-y-4 sm:space-y-6">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                            <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-blue-200">
                              <div className="flex items-center mb-2 sm:mb-3">
                                <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2" />
                                <p className="text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wide">Nombre del Negocio</p>
                              </div>
                              <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">{user.store.name}</p>
                            </div>
                            <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-green-200">
                              <div className="flex items-center mb-2 sm:mb-3">
                                <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mr-2" />
                                <p className="text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wide">Estado</p>
                              </div>
                              <span className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold ${
                                user.store.status === 'approved'
                                  ? 'bg-green-200 text-green-800'
                                  : user.store.status === 'pending'
                                  ? 'bg-yellow-200 text-yellow-800'
                                  : 'bg-red-200 text-red-800'
                              }`}>
                                {user.store.status === 'approved' ? 'Aprobado' :
                                 user.store.status === 'pending' ? 'Pendiente' : 'Rechazado'}
                              </span>
                            </div>
                          </div>
                          {user.store.address && (
                            <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100">
                              <div className="flex items-center mb-2 sm:mb-3">
                                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 mr-2" />
                                <p className="text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wide">Dirección</p>
                              </div>
                              <p className="text-sm sm:text-base lg:text-lg font-medium text-gray-900">{user.store.address}</p>
                            </div>
                          )}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                            {user.store.phone && (
                              <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100">
                                <div className="flex items-center mb-2 sm:mb-3">
                                  <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 mr-2" />
                                  <p className="text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wide">Teléfono</p>
                                </div>
                                <p className="text-sm sm:text-base lg:text-lg font-medium text-gray-900">{user.store.phone}</p>
                              </div>
                            )}
                            {user.store.email && (
                              <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100">
                                <div className="flex items-center mb-2 sm:mb-3">
                                  <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 mr-2" />
                                  <p className="text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wide">Email</p>
                                </div>
                                <p className="text-sm sm:text-base lg:text-lg font-medium text-gray-900 break-all">{user.store.email}</p>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center justify-center pt-2 sm:pt-4">
                            <span className={`inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base font-bold shadow-sm ${
                              user.store.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {user.store.is_active ? '✓ Negocio Activo' : '✗ Negocio Inactivo'}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-amber-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border-2 border-amber-200">
                          <div className="flex items-start">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-100 rounded-lg sm:rounded-xl flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-base sm:text-lg font-bold text-amber-900 mb-2">Sin Negocio Asignado</h3>
                              <p className="text-sm sm:text-base text-amber-800 mb-3 sm:mb-4">
                                Este usuario no tiene un negocio asignado actualmente.
                              </p>
                              {(user.type === 'store_admin' || user.type === 'dispatcher' || user.type === 'chef' || user.type === 'delivery') && (
                                <button
                                  onClick={handleEdit}
                                  className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-amber-600 text-white rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold hover:bg-amber-700 transition-colors shadow-sm"
                                >
                                  Asignar Negocio
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
              </div>

                {/* Right Column - Statistics & Actions - iMac Style - Mobile Responsive */}
                <div className="space-y-4 sm:space-y-6 lg:space-y-8">
                  {/* Statistics Section - iMac Style - Mobile Responsive */}
                  <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 p-4 sm:p-6 lg:p-8 xl:p-12">
                    <div className="flex items-center mb-4 sm:mb-6 lg:mb-8 pb-4 sm:pb-6 border-b border-gray-200">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-50 rounded-xl sm:rounded-2xl flex items-center justify-center mr-3 sm:mr-4">
                        <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
                      </div>
                      <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Estadísticas</h2>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
                      {(user.type === 'customer' || user.type === 'store_owner') && (
                        <>
                          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center border border-blue-200 shadow-sm">
                            <Package className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600 mx-auto mb-2 sm:mb-3" />
                            <p className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">{user.total_orders || 0}</p>
                            <p className="text-xs sm:text-sm font-semibold text-gray-600">Total Pedidos</p>
                          </div>
                          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center border border-green-200 shadow-sm">
                            <DollarSign className="w-8 h-8 sm:w-10 sm:h-10 text-green-600 mx-auto mb-2 sm:mb-3" />
                            <p className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                              RD${user.total_spent ? Number(user.total_spent).toFixed(0) : '0'}
                            </p>
                            <p className="text-xs sm:text-sm font-semibold text-gray-600">Total Gastado</p>
                          </div>
                        </>
                      )}
                      {(user.type === 'chef' || user.type === 'delivery' || user.type === 'dispatcher') && (
                        <>
                          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center border border-blue-200 shadow-sm">
                            <Package className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600 mx-auto mb-2 sm:mb-3" />
                            <p className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">{user.employee_orders || 0}</p>
                            <p className="text-xs sm:text-sm font-semibold text-gray-600">Total Pedidos</p>
                          </div>
                          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center border border-green-200 shadow-sm">
                            <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-green-600 mx-auto mb-2 sm:mb-3" />
                            <p className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">{user.completed_orders_30d || 0}</p>
                            <p className="text-xs sm:text-sm font-semibold text-gray-600">Completados (30d)</p>
                          </div>
                        </>
                      )}
                      {user.type === 'store_owner' && (
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center border border-purple-200 shadow-sm">
                          <Store className="w-8 h-8 sm:w-10 sm:h-10 text-purple-600 mx-auto mb-2 sm:mb-3" />
                          <p className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                            {user.owned_stores ? user.owned_stores.length : (user.store ? 1 : 0)}
                          </p>
                          <p className="text-xs sm:text-sm font-semibold text-gray-600">Negocios</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Account Status - iMac Style - Mobile Responsive */}
                  <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 p-4 sm:p-6 lg:p-8 xl:p-12">
                    <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 pb-3 sm:pb-4 border-b border-gray-200">Estado de Cuenta</h2>
                    <div className="space-y-3 sm:space-y-4">
                      <div className="flex items-center justify-between py-2 sm:py-3">
                        <span className="text-sm sm:text-base font-medium text-gray-700">Estado</span>
                        <span className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold ${
                          user.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {user.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-2 sm:py-3">
                        <span className="text-sm sm:text-base font-medium text-gray-700">Email Verificado</span>
                        <span className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold ${
                          user.email_verified
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {user.email_verified ? 'Sí' : 'No'}
                        </span>
                      </div>
                      {user.type === 'delivery' && (
                        <div className="flex items-center justify-between py-2 sm:py-3">
                          <span className="text-sm sm:text-base font-medium text-gray-700">Disponible para Entrega</span>
                          <span className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold ${
                            user.delivery_available
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.delivery_available ? 'Sí' : 'No'}
                          </span>
                        </div>
                      )}
                      {user.type !== 'platform_admin' && (
                        <div className="flex items-center justify-between py-2 sm:py-3">
                          <span className="text-sm sm:text-base font-medium text-gray-700">Acceso a Desarrollo</span>
                          <span className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold ${
                            (user.development_access || false)
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {(user.development_access || false) ? '🔧 Sí' : 'No'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions - iMac Style - Mobile Responsive */}
                  <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 p-4 sm:p-6 lg:p-8 xl:p-12">
                    <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 pb-3 sm:pb-4 border-b border-gray-200">Acciones</h2>
                    <div className="space-y-3 sm:space-y-4">
                      {user.is_active ? (
                        <button
                          onClick={() => handleStatusChange(user.id, false)}
                          className="w-full flex items-center justify-center px-4 sm:px-6 py-3 sm:py-4 bg-red-50 rounded-xl text-sm sm:text-base font-semibold text-red-700 border-2 border-red-200 hover:bg-red-100 transition-all shadow-sm hover:shadow-md active:scale-95"
                        >
                          <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                          Suspender Usuario
                        </button>
                      ) : (
                        <button
                          onClick={() => handleStatusChange(user.id, true)}
                          className="w-full flex items-center justify-center px-4 sm:px-6 py-3 sm:py-4 bg-green-50 rounded-xl text-sm sm:text-base font-semibold text-green-700 border-2 border-green-200 hover:bg-green-100 transition-all shadow-sm hover:shadow-md active:scale-95"
                        >
                          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                          Activar Usuario
                        </button>
                      )}

                      {user.type !== 'platform_admin' && (
                        <div className="pt-3 sm:pt-4 border-t border-gray-200">
                          <label className="block text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3">
                            Cambiar Rol
                          </label>
                          <select
                            value={user.type}
                            onChange={(e) => handleRoleChange(user.id, e.target.value as UserDetails['type'])}
                            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 text-sm sm:text-base font-medium bg-white"
                          >
                            <option value="customer">Cliente</option>
                            <option value="store_owner">Dueño de Colmado</option>
                            <option value="store_admin">Admin de Colmado</option>
                            <option value="dispatcher">Despachador</option>
                            <option value="chef">Cocinero</option>
                            <option value="delivery">Repartidor</option>
                          </select>
                        </div>
                      )}

                      <div className="pt-3 sm:pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="block text-sm sm:text-base font-semibold text-gray-900 mb-1">
                              Acceso a Desarrollo
                            </label>
                            <p className="text-xs sm:text-sm text-gray-500">
                              Permite ver negocios en modo desarrollo
                            </p>
                          </div>
                          <button
                            onClick={() => handleDevelopmentAccessChange(user.id, !(user.development_access || false))}
                            disabled={!user}
                            className={`relative inline-flex h-6 w-11 sm:h-7 sm:w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                              (user.development_access || false) ? 'bg-purple-600' : 'bg-gray-200'
                            }`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-5 w-5 sm:h-6 sm:w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                (user.development_access || false) ? 'translate-x-5 sm:translate-x-6' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default UserDetails
